import { useAuth } from '../contexts/AuthContext';
import { getAvailableLiveSessions as getLiveSessionsByAccess, LiveSession } from '../data/liveSessions';
import { getAvailableModulesByDate as getModulesByDate, getModuleAvailabilityStatus, Module } from '../data/modules';

export const useCourseAccess = () => {
  const { user } = useAuth();
  
  const courseAccess = user?.courseAccess || '';
  const userBatch = Number(user?.batch) || 10; // Ensure it's a number and default to current batch
  
  // Check specific access levels
  const isUni = courseAccess === 'Uni';
  const isFirst = courseAccess === 'First';
  const isManager = courseAccess === 'Manager';
  
  // Check if user is from previous batches (lifetime access)
  const isPreviousBatch = userBatch <= 9;
  const isCurrentBatch = userBatch === 10;
  
  // Check module access based on courseAccess level
  const hasModuleAccess = (moduleNumber: number): boolean => {
    switch (courseAccess) {
      case 'Uni':
        return moduleNumber <= 2;
      case 'First':
        return moduleNumber <= 4;
      case 'Manager':
        return moduleNumber <= 5;
      default:
        return false;
    }
  };
  
  // Get available modules for current access level (legacy function for backward compatibility)
  const getAvailableModules = (): number[] => {
    switch (courseAccess) {
      case 'Uni':
        return [1, 2];
      case 'First':
        return [1, 2, 3, 4];
      case 'Manager':
        return [1, 2, 3, 4, 5];
      default:
        return [];
    }
  };

  // Get available modules based on access level and current date
  const getAvailableModulesByDate = (): Module[] => {
    return getModulesByDate(courseAccess, userBatch);
  };

  // Get module availability status
  const getModuleStatus = (module: Module) => {
    return getModuleAvailabilityStatus(module, userBatch);
  };

  // Get available live sessions for current access level and batch
  const getAvailableLiveSessions = (): LiveSession[] => {
    return getLiveSessionsByAccess(courseAccess, userBatch);
  };
  
  // Get access level display name
  const getAccessLevelName = (): string => {
    const batchSuffix = ` (Batch ${userBatch})`;
    switch (courseAccess) {
      case 'Uni':
        return `University Students Level${batchSuffix}`;
      case 'First':
        return `First Jobbers Level${batchSuffix}`;
      case 'Manager':
        return `Manager Level${batchSuffix}`;
      default:
        return 'No Access';
    }
  };
  
  // Get access level description
  const getAccessLevelDescription = (): string => {
    switch (courseAccess) {
      case 'Uni':
        return 'Secure Double-Digit Salary before Graduation';
      case 'First':
        return '2x Salary in 3 Years PLUS 5x Salary in 10 years';
      case 'Manager':
        return 'Penetrate Executive Status & Triple Digit Salary';
      default:
        return 'No course access available';
    }
  };
  
  return {
    courseAccess,
    userBatch,
    hasCourseAccess: !!courseAccess,
    isUni,
    isFirst,
    isManager,
    isPreviousBatch,
    isCurrentBatch,
    hasModuleAccess,
    getAvailableModules,
    getAvailableModulesByDate,
    getModuleStatus,
    getAvailableLiveSessions,
    getAccessLevelName,
    getAccessLevelDescription
  };
}; 